import { useParams, Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <div className="pt-24 text-center py-20">
        <h2 className="text-xl text-foreground-200">电影未找到</h2>
        <Link to="/" className="text-primary-500 hover:text-primary-400 mt-4 inline-block">返回首页</Link>
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 6);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://tiantiancinema.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: movie.category,
        item: `https://tiantiancinema.com/category/${movie.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: movie.title,
        item: `https://tiantiancinema.com/movie/${movie.id}`,
      },
    ],
  };

  const movieSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: movie.releaseDate,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((name) => ({
      '@type': 'Person',
      name,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      ratingCount: Math.floor(Math.random() * 50000) + 10000,
    },
    genre: [movie.category],
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
  };

  return (
    <>
      <PageSEO
        title={`${movie.title} - ${movie.category} | 天天影院`}
        description={movie.description}
        keywords={`${movie.title},${movie.category},${movie.tags.join(',')},天天影院`}
        canonicalUrl={`https://tiantiancinema.com/movie/${movie.id}`}
        ogTitle={`${movie.title} | 天天影院`}
        ogDescription={movie.description}
        ogUrl={`https://tiantiancinema.com/movie/${movie.id}`}
        structuredData={[breadcrumbSchema, movieSchema]}
      />

      <Breadcrumb />

      <main className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pb-16">
        <article className="flex flex-col lg:flex-row gap-8 md:gap-10">
          <div className="shrink-0 w-full lg:w-[340px]">
            <div className="relative aspect-[2/3] rounded-xl overflow-hidden">
              <img
                src={movie.poster}
                alt={movie.title}
                title={`${movie.title} - 天天影院${movie.category}`}
                className="w-full h-full object-cover object-top"
              />
              {movie.isNew && (
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-accent-500 text-background-950 text-xs font-semibold whitespace-nowrap">
                  新上映
                </span>
              )}
              {movie.isHot && (
                <span className="absolute top-4 right-4 px-3 py-1 rounded-full bg-primary-500 text-background-950 text-xs font-semibold whitespace-nowrap">
                  热播
                </span>
              )}
            </div>

            <div className="mt-5 p-5 rounded-xl bg-background-100 border border-background-300/30">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/15">
                  <i className="ri-star-fill text-primary-500"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground-50">{movie.rating}</div>
                  <div className="text-xs text-foreground-500">豆瓣评分</div>
                </div>
              </div>

              <a
                href="#"
                className="flex items-center justify-center gap-2 w-full py-3 whitespace-nowrap rounded-full bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer mb-3"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-play-fill"></i>
                </div>
                立即播放
              </a>

              <a
                href="#"
                className="flex items-center justify-center gap-2 w-full py-3 whitespace-nowrap rounded-full border border-background-300/50 text-foreground-300 text-sm hover:border-foreground-400 hover:text-foreground-200 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-heart-line"></i>
                </div>
                加入收藏
              </a>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-1">
              {movie.title}
            </h1>
            <p className="text-sm text-foreground-500 mb-4">{movie.engTitle}</p>

            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="px-2.5 py-1 rounded-full bg-primary-500/20 text-primary-400 text-xs font-medium whitespace-nowrap">
                {movie.category}
              </span>
              {movie.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 border border-background-300/30 text-foreground-400 text-xs whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 p-4 rounded-xl bg-background-100 border border-background-300/20">
              <div>
                <span className="text-xs text-foreground-600">导演</span>
                <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.director}</p>
              </div>
              <div>
                <span className="text-xs text-foreground-600">主演</span>
                <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.actors.join(' / ')}</p>
              </div>
              <div>
                <span className="text-xs text-foreground-600">类型</span>
                <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.category}</p>
              </div>
              <div>
                <span className="text-xs text-foreground-600">地区</span>
                <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.region}</p>
              </div>
              <div>
                <span className="text-xs text-foreground-600">片长</span>
                <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.duration}</p>
              </div>
              <div>
                <span className="text-xs text-foreground-600">上映日期</span>
                <p className="text-sm font-medium text-foreground-200 mt-0.5">{movie.releaseDate}</p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-lg font-heading font-semibold text-foreground-100 mb-3">剧情简介</h2>
              <p className="text-sm text-foreground-400 leading-relaxed">
                {movie.longDescription}
              </p>
            </div>
          </div>
        </article>

        {relatedMovies.length > 0 && (
          <section className="mt-12 pt-10 border-t border-background-300/20">
            <div className="flex items-center justify-between mb-5 md:mb-6">
              <h3 className="text-lg font-heading font-bold text-foreground-50">
                相关推荐
              </h3>
              <Link
                to={`/category/${movie.categorySlug}`}
                className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
              >
                更多{movie.category}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {relatedMovies.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}